import React, { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, QrCode, Search } from "lucide-react";

export default function AdminQrCards() {
  const [students, setStudents] = useState([]);
  const [angkatans, setAngkatans] = useState([]);
  const [fUnit, setFUnit] = useState("all");
  const [fAng, setFAng] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/angkatans").then((r) => setAngkatans(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => students.filter((s) =>
      (fUnit === "all" || s.gender === fUnit) &&
      (fAng === "all" || String(s.angkatan) === fAng) &&
      (s.name.toLowerCase().includes(query.toLowerCase()) || (s.nisn || "").includes(query))
    ),
    [students, fUnit, fAng, query]
  );

  return (
    <div>
      <PageHeader
        title="Kartu QR Santri"
        subtitle="Cetak kartu QR untuk identifikasi santri di Kopontren. QR berisi NISN santri."
        action={
          <Button onClick={() => window.print()} data-testid="print-qr-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl no-print">
            <Printer size={18} className="mr-2" /> Cetak ({filtered.length})
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3 mb-6 no-print">
        <Select value={fUnit} onValueChange={setFUnit}>
          <SelectTrigger data-testid="qr-filter-unit" className="w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Unit</SelectItem>
            <SelectItem value="putra">Putra</SelectItem>
            <SelectItem value="putri">Putri</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fAng} onValueChange={setFAng}>
          <SelectTrigger data-testid="qr-filter-angkatan" className="w-44 rounded-xl bg-white"><SelectValue placeholder="Angkatan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Angkatan</SelectItem>
            {angkatans.map((a) => <SelectItem key={a} value={String(a)}>Angkatan {a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="qr-search" placeholder="Cari nama / NISN…" className="w-full pl-10 h-10 rounded-xl bg-white border border-stone-200 text-sm outline-none focus:border-emerald-400" />
        </div>
      </div>

      {loading ? (
        <p className="text-stone-400">Memuat…</p>
      ) : filtered.length === 0 ? (
        <Empty><QrCode className="mx-auto mb-2" /> Tidak ada santri sesuai filter.</Empty>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`qr-card-${s.id}`} className="qr-card bg-white border border-stone-200 rounded-2xl p-4 shadow-sm text-center">
              <p className="font-heading font-bold text-stone-900 leading-tight truncate">{s.name}</p>
              <p className="text-[11px] text-stone-400 font-mono mb-3">NISN {s.nisn}</p>
              <div className="flex justify-center">
                <QRCodeCanvas value={s.nisn} size={128} level="M" includeMargin />
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full font-semibold ${s.gender === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{s.gender}</span>
                <span className="text-stone-400">Angkatan {s.angkatan}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
